import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  const [agreed, setAgreed] = useState(false);
  const navigate = useNavigate();

  const handleAgreeAndEnter = () => {
    if (agreed) {
      // Store agreement in localStorage
      localStorage.setItem('tcm_disclaimer_agreed', 'true');
      // Navigate to dashboard (anonymous viewing)
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 fade-in">
            <div className="inline-block mb-6">
              <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-600 via-cyan-600 to-emerald-600">
                TCM Study Club
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Master Traditional Chinese Diagnosis through collaborative case studies and comprehensive testing. Practice with simple cases for comps and complex scenarios for boards and clinic readiness. Enjoy all access absolutely free.
            </p>
            
            {/* Disclaimer and Entry */}
            <div className="max-w-2xl mx-auto bg-white rounded-2xl p-8 shadow-xl">
              <div className="mb-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <input
                    type="checkbox"
                    id="disclaimer"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 w-6 h-6 sm:w-5 sm:h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer flex-shrink-0"
                  />
                  <label htmlFor="disclaimer" className="text-left text-gray-700 leading-relaxed cursor-pointer">
                    I understand and agree that the content and discussions on this platform do not constitute medical or professional advice. All information is for educational purposes only.
                  </label>
                </div>
              </div>
              
              <Button
                onClick={handleAgreeAndEnter}
                disabled={!agreed}
                className={`w-full py-6 text-lg rounded-full shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  agreed 
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white hover:shadow-2xl' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Agree and Enter
              </Button>
              
              <p className="mt-4 text-sm text-gray-500">
                Create a forum account inside to post comments and take tests
              </p>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-24 mb-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 card-hover" data-testid="feature-card-studies">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Dual Case Studies</h3>
              <p className="text-gray-600 leading-relaxed">
                Two case studies every two weeks: one simple for boards prep, one complex for clinic readiness.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 card-hover" data-testid="feature-card-discussion">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Forum Discussions</h3>
              <p className="text-gray-600 leading-relaxed">
                Engage with peers, share insights, and learn collaboratively through threaded discussions.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 card-hover" data-testid="feature-card-testing">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Comprehensive Testing</h3>
              <p className="text-gray-600 leading-relaxed">
                Practice with timed MCQ tests covering diagnosis, treatment principles, and point selection for exam prep.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 card-hover" data-testid="feature-card-progress">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Track Progress</h3>
              <p className="text-gray-600 leading-relaxed">
                Monitor your learning journey with quizzes, saved cases, and completion tracking.
              </p>
            </div>
          </div>

          {/* New: Video Library Highlight */}
          <div className="mt-20" data-testid="video-library-highlight">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-rose-600 via-red-600 to-red-700 shadow-2xl">
              <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-20 -left-10 w-72 h-72 bg-black/10 rounded-full blur-2xl"></div>
              <div className="relative grid md:grid-cols-2 gap-10 items-center p-10 md:p-14">
                <div>
                  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-700 bg-white px-3 py-1 rounded-full mb-5">
                    <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>
                    New Feature
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Diagnosis Video Library
                  </h2>
                  <p className="text-red-50 text-lg leading-relaxed mb-6">
                    Learn by watching. Our growing library of short, focused videos walks you through real TCM
                    diagnosis skills—reading the tongue, taking the pulse, and reasoning through case studies—organized
                    by topic so you can find exactly what you need.
                  </p>
                  <ul className="space-y-3 mb-2">
                    <li className="flex items-center text-white">
                      <svg className="w-5 h-5 mr-3 text-red-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Bite-sized lessons on diagnosis, tongue &amp; pulse</span>
                    </li>
                    <li className="flex items-center text-white">
                      <svg className="w-5 h-5 mr-3 text-red-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Filter by topic and watch right in the browser</span>
                    </li>
                    <li className="flex items-center text-white">
                      <svg className="w-5 h-5 mr-3 text-red-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>New videos added regularly—log in to start watching</span>
                    </li>
                  </ul>
                </div>
                <div className="flex justify-center md:justify-end">
                  <div className="relative w-full max-w-sm aspect-video bg-black/40 rounded-2xl shadow-xl border border-white/20 flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/30 to-transparent rounded-2xl"></div>
                    <div className="relative w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-9 h-9 text-red-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* Additional Info */}
          <div className="mt-24 bg-white rounded-3xl p-12 shadow-xl">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Learn Through Practice
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Our structured approach helps you build confidence from basic board questions to complex clinical scenarios. Each case includes detailed discussions, quizzes, and timed solution reveals.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-teal-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Submit your own case studies for peer review</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-teal-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Interactive quizzes to test your knowledge</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-teal-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Solutions revealed after discussion period</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-teal-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Tag-based organization and powerful search</span>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-teal-100 to-cyan-100 rounded-2xl p-8 transform rotate-3 absolute inset-0 opacity-50"></div>
                <div className="bg-white rounded-2xl p-8 relative z-10 border-2 border-teal-300 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Weekly Schedule
                  </h3>
                  
                  <div className="space-y-5">
                    <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-l-4 border-green-400">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-400 rounded-full flex items-center justify-center text-white font-bold">1</div>
                        <span className="text-base font-semibold text-gray-900">Simple Case Study</span>
                      </div>
                      <span className="text-sm font-bold text-green-700 bg-green-100 px-3 py-1 rounded-full">Weekly</span>
                    </div>
                    
                    <div className="flex items-center justify-between bg-gradient-to-r from-red-50 to-rose-50 rounded-xl p-4 border-l-4 border-red-400">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-red-400 rounded-full flex items-center justify-center text-white font-bold">1</div>
                        <span className="text-base font-semibold text-gray-900">Complex Case Study</span>
                      </div>
                      <span className="text-sm font-bold text-red-700 bg-red-100 px-3 py-1 rounded-full">Every 2 Weeks</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-center text-sm text-teal-700 italic font-medium">
                      "Structured learning for comprehensive board and clinic preparation."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* TCM Quote Section */}
          <div className="mt-24 bg-gradient-to-br from-teal-600 via-cyan-600 to-emerald-600 rounded-3xl p-12 shadow-xl">
            <div className="text-center">
              <div className="relative">
                {/* Decorative quote marks */}
                <div className="absolute -top-8 left-0 text-8xl text-white opacity-20" style={{ fontFamily: 'Georgia, serif' }}>"</div>
                <div className="relative z-10 py-8">
                  <p className="text-4xl md:text-5xl font-bold text-white mb-6 italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                    It depends...
                  </p>
                  <div className="w-24 h-1 bg-white opacity-50 mx-auto mb-6"></div>
                  <p className="text-xl text-teal-100 font-medium">
                    — Chinese Medicine
                  </p>
                  <p className="text-sm text-teal-200 mt-4 italic max-w-2xl mx-auto">
                    The most honest answer in TCM. Every diagnosis, every treatment, every point selection... it depends on the patient, the presentation, the season, and countless other factors.
                  </p>
                </div>
                <div className="absolute -bottom-8 right-0 text-8xl text-white opacity-20" style={{ fontFamily: 'Georgia, serif' }}>"</div>
              </div>
            </div>
          </div>

          {/* Badge System Section */}
          <div className="mt-24 bg-gradient-to-br from-purple-600 via-pink-600 to-indigo-600 rounded-3xl p-12 shadow-xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                🏆 Level Up Your TCM Journey
              </h2>
                <p className="text-xl text-purple-100 max-w-3xl mx-auto">
                  Earn badges and climb the ranks as you contribute to discussions. Share your clinical insights and reach the ultimate Immortal level!
                </p>
                <div className="mt-4 flex items-center justify-center space-x-6 text-purple-100">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-yellow-300">👑 Grand Master</p>
                    <p className="text-sm">10,000 posts</p>
                  </div>
                  <div className="text-2xl">→</div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-pink-300">✨ Immortal</p>
                    <p className="text-sm">20,000 posts</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                {/* How It Works */}
                <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8 border border-white border-opacity-20">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <span className="mr-3">📊</span> How It Works
                  </h3>
                  <div className="space-y-4 text-purple-50">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white font-bold">1</span>
                      </div>
                      <div>
                        <p className="font-semibold text-white">Share Your Insights</p>
                        <p className="text-sm">Post comments discussing diagnosis, treatment principles, and point selections</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white font-bold">2</span>
                      </div>
                      <div>
                        <p className="font-semibold text-white">Earn Experience</p>
                        <p className="text-sm">Each discussion post earns XP towards your next level</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white font-bold">3</span>
                      </div>
                      <div>
                        <p className="font-semibold text-white">Unlock Badges</p>
                        <p className="text-sm">Reach new badge tiers every 10 levels and show off your expertise</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Badge Tiers Preview */}
                <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8 border border-white border-opacity-20">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <span className="mr-3">✨</span> Badge Tiers
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                      <div className="text-2xl mb-1">🌱</div>
                      <p className="text-xs font-bold text-white">Novice Scholar</p>
                      <p className="text-xs text-purple-200">Levels 1-10</p>
                    </div>
                    <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                      <div className="text-2xl mb-1">📚</div>
                      <p className="text-xs font-bold text-white">Apprentice</p>
                      <p className="text-xs text-purple-200">Levels 11-20</p>
                    </div>
                    <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                      <div className="text-2xl mb-1">⚡</div>
                      <p className="text-xs font-bold text-white">Practitioner</p>
                      <p className="text-xs text-purple-200">Levels 21-30</p>
                    </div>
                    <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                      <div className="text-2xl mb-1">💎</div>
                      <p className="text-xs font-bold text-white">Expert</p>
                      <p className="text-xs text-purple-200">Levels 31-40</p>
                    </div>
                    <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                      <div className="text-2xl mb-1">💠</div>
                      <p className="text-xs font-bold text-white">Master</p>
                      <p className="text-xs text-purple-200">Levels 41-50</p>
                    </div>
                    <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                      <div className="text-2xl mb-1">👑</div>
                      <p className="text-xs font-bold text-white">Grand Master</p>
                      <p className="text-xs text-purple-200">Levels 91-100</p>
                    </div>
                    <div className="col-span-2 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-opacity-30 rounded-lg p-4 text-center border-2 border-pink-300">
                      <div className="text-3xl mb-1">✨</div>
                      <p className="text-sm font-bold text-white">IMMORTAL</p>
                      <p className="text-xs text-pink-200">Levels 101-120 • Ultimate Achievement!</p>
                    </div>
                  </div>
                  <p className="text-xs text-purple-100 mt-4 text-center italic">
                    Grand Master = 10,000 posts • Immortal = 20,000 posts
                  </p>
                </div>
              </div>

              <div className="text-center">
                <p className="text-2xl font-bold text-white mb-4">
                  Ready to start your journey?
                </p>
                <button
                  onClick={() => window.socialLogin()}
                  className="bg-white text-purple-600 hover:bg-purple-50 font-bold px-12 py-4 rounded-full text-lg shadow-2xl transform transition-all duration-200 hover:scale-105"
                >
                  <span className="flex items-center justify-center">
                    <svg className="w-6 h-6 mr-2" viewBox="0 0 48 48">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      <path fill="none" d="M0 0h48v48H0z"/>
                    </svg>
                    Join with Google
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default LandingPage;